import { Component, OnInit } from '@angular/core';
import { QuestionComponent } from '../../shared/components/question/question.component';
import { CommonModule, NgFor, NgIf } from '@angular/common';
import { QuestionResponse, Question, FilterOption, FilterOptions } from '../../models/question.model';
import { QuestionService } from '../../services/question.service';
import { Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { UserService } from '../../services/user.service';
import { HttpParams } from '@angular/common/http';

@Component({
  selector: 'app-questions',
  templateUrl: './questions.component.html',
  standalone: true,
  imports: [QuestionComponent, NgFor, NgIf, FormsModule, RouterModule, CommonModule],
  styleUrls: ['./questions.component.css']
})
export class QuestionsComponent implements OnInit {
  questions: Question[] = [];
  years: number[] = [];
  isLoading: boolean = true;
  currentPage: number = 1;
  totalPages: number = 0;
  hasAccess: boolean = false;
  isPlanChecked: boolean = false;

  filterOptions: FilterOptions = {
    positions: [],
    bancas: [],
    subjects: [],
    topics: []
  };

  filters = {
    questionType: '',
    difficulty: '',
    positions: '',
    bancas: '',
    subjects: '',
    topics: '',
    year: '',
    answerStatus: 'all' // Novo filtro
  };

  get totalPagesArray(): (number | string)[] {
    const pages: (number | string)[] = [];

    const delta = 5; // Quantas antes e depois
    const rangeStart = Math.max(2, this.currentPage - delta);
    const rangeEnd = Math.min(this.totalPages - 1, this.currentPage + delta);

    pages.push(1); // Sempre mostra a primeira

    if (rangeStart > 2) {
      pages.push('...');
    }

    for (let i = rangeStart; i <= rangeEnd; i++) {
      pages.push(i);
    }

    if (rangeEnd < this.totalPages - 1) {
      pages.push('...');
    }

    if (this.totalPages > 1) {
      pages.push(this.totalPages); // Sempre mostra a última
    }

    return pages;
  }

  constructor(
    private questionService: QuestionService,
    private router: Router,
    private userService: UserService
  ) { }

  ngOnInit() {
    const currentYear = new Date().getFullYear();
    for (let year = 1950; year <= currentYear + 10; year++) {
      this.years.push(year);
    }

    const userId = Number(localStorage.getItem('id'));
    if (!userId) {
      this.isPlanChecked = true;
      return;
    }

    const localPlan = localStorage.getItem('plan');

    if (localPlan) {
      const plan = localPlan.toLowerCase();
      this.hasAccess = plan !== 'free';
      this.isPlanChecked = true;

      if (this.hasAccess) {
        this.loadFilterOptions().then(() => {
          this.loadQuestions(this.currentPage);
        });
      }
    } else {
      this.userService.getUserPlan(userId).subscribe({
        next: (response) => {
          const plan = response?.plan?.toLowerCase();
          localStorage.setItem('plan', plan); // salva no localStorage

          this.hasAccess = plan !== 'free';
          this.isPlanChecked = true;

          if (this.hasAccess) {
            this.loadFilterOptions().then(() => {
              this.loadQuestions(this.currentPage);
            });
          }
        },
        error: (error) => {
          console.error('Erro ao verificar o plano do usuário:', error);
          this.isPlanChecked = true;
        }
      });
    }
  }

  private loadFilterOptions(): Promise<void> {
    return new Promise((resolve) => {
      this.questionService.getAttributes().subscribe({
        next: (response: FilterOptions) => {
          this.filterOptions = response;
          resolve();
        },
        error: (error) => {
          console.error('Erro ao carregar opções de filtro', error);
          this.filterOptions = {
            positions: [],
            bancas: [],
            subjects: [],
            topics: []
          };
          resolve();
        }
      });
    });
  }

  loadQuestions(page: number) {
    this.isLoading = true;
    
    // Use o endpoint diferente se estiver filtrando por status de resposta
    if (this.filters.answerStatus !== 'all') {
      this.questionService.getUserAnsweredQuestions(page, this.filters).subscribe({
        next: (response: QuestionResponse) => {
          this.questions = response.questions.data;
          this.mapNamesToQuestions();
          this.currentPage = response.questions.current_page;
          this.totalPages = response.questions.last_page;
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Erro ao carregar as perguntas', error);
          this.isLoading = false;
        }
      });
    } else {
      // Use o endpoint normal para "Todas as questões"
      this.questionService.getQuestions(page, this.filters).subscribe({
        next: (response: QuestionResponse) => {
          this.questions = response.questions.data;
          this.mapNamesToQuestions();
          this.currentPage = response.questions.current_page;
          this.totalPages = response.questions.last_page;
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Erro ao carregar as perguntas', error);
          this.isLoading = false;
        }
      });
    }
  }

  private mapNamesToQuestions() {
    this.questions = this.questions.map(question => {
      return {
        ...question,
        positionName: this.getNameById(this.filterOptions.positions, question.positions),
        bancaName: this.getNameById(this.filterOptions.bancas, question.bancas),
        subjectName: this.getNameById(this.filterOptions.subjects, question.subjects),
        topicName: this.getNameById(this.filterOptions.topics, question.topics)
      };
    });
  }

  private getNameById(options: FilterOption[], id: string): string {
    if (!id || !options) return '';
    const found = options.find(option => option.id === id);
    return found ? found.name : 'Não encontrado';
  }

  get filterText() {
    const filtersArray = [];
    
    // Adicione o novo filtro de status de resposta
    if (this.filters.answerStatus !== 'all') {
      const statusMap: {[key: string]: string} = {
        'answered': 'Resolvidas',
        'not_answered': 'Não resolvidas',
        'correct': 'Que acertei',
        'incorrect': 'Que errei'
      };
      filtersArray.push(statusMap[this.filters.answerStatus]);
    }
    
    // Mantenha os filtros existentes
    if (this.filters.year) filtersArray.push(`Ano: ${this.filters.year}`);
    if (this.filters.bancas) {
      const banca = this.filterOptions.bancas.find(b => b.id === this.filters.bancas);
      filtersArray.push(`Banca: ${banca?.name || this.filters.bancas}`);
    }
    if (this.filters.questionType) filtersArray.push(`Tipo: ${this.filters.questionType}`);
    if (this.filters.difficulty) filtersArray.push(`Dificuldade: ${this.filters.difficulty}`);
    if (this.filters.positions) {
      const position = this.filterOptions.positions.find(p => p.id === this.filters.positions);
      filtersArray.push(`Cargo: ${position?.name || this.filters.positions}`);
    }
    if (this.filters.subjects) {
      const subject = this.filterOptions.subjects.find(s => s.id === this.filters.subjects);
      filtersArray.push(`Disciplina: ${subject?.name || this.filters.subjects}`);
    }
    if (this.filters.topics) {
      const topic = this.filterOptions.topics.find(t => t.id === this.filters.topics);
      filtersArray.push(`Assunto: ${topic?.name || this.filters.topics}`);
    }

    return filtersArray.join(', ');
  }

  applyFilters() {
    const queryParams = {
      question_type: this.filters.questionType,
      difficulty: this.filters.difficulty,
      positions: this.filters.positions,
      bancas: this.filters.bancas,
      subjects: this.filters.subjects,
      topics: this.filters.topics,
      year: this.filters.year,
      answer_status: this.filters.answerStatus !== 'all' ? this.filters.answerStatus : null
    };

    this.router.navigate([], {
      queryParams: queryParams,
      queryParamsHandling: 'merge'
    });

    this.loadQuestions(1);
  }

  goToPage(page: number | string) {
    if (typeof page !== 'number') return;
    if (page > 0 && page <= this.totalPages) {
      this.loadQuestions(page);
    }
  }

  clearFilters() {
    this.filters = {
      questionType: '',
      difficulty: '',
      positions: '',
      bancas: '',
      subjects: '',
      topics: '',
      year: '',
      answerStatus: 'all'
    };

    this.applyFilters();
  }
}